import _ from 'underscore';
import classNames from 'classnames';
import { merge, cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import React from 'react';
import PropTypes from 'prop-types';
import { layoutConfigs } from './layout-configs';
import { LayoutContext } from './layout-context';
import { StylesContainer } from './styles-container';
import { names } from './components';
import { Mode } from './mode';
import { styles } from './styles';

class LayoutContainer extends React.PureComponent {
  static propTypes = {
    children: PropTypes.node.isRequired,
    persistConfig: PropTypes.objectOf(PropTypes.shape({
      mode: PropTypes.oneOf(_.values(Mode)),
    })),
    pageConfig: PropTypes.objectOf(PropTypes.shape({
      mode: PropTypes.oneOf(_.values(Mode)),
    })),
    controlledByProps: PropTypes.bool,
    isGridLayout: PropTypes.bool,
    classes: PropTypes.shape({
      container: PropTypes.string,
    }).isRequired,
  };

  static defaultProps = {
    persistConfig: layoutConfigs.default,
    pageConfig: layoutConfigs.default,
    controlledByProps: false,
    isGridLayout: false,
  };

  constructor(props) {
    super(props);

    if (!props.isGridLayout) {
      this.stylesContainer = new StylesContainer(props.persistConfig);
    }

    this.state = {
      persistConfig: props.persistConfig,
      pageConfig: props.pageConfig,
      controlledByProps: props.controlledByProps,
      isLayoutLocked: false,
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (!prevState.controlledByProps) {
      return null;
    }
    if (prevState.ownUpdate) {
      // getDerivedStateFromProps is triggered before each render.
      // need to ensure that we dont override changes from its own update
      return { ownUpdate: false };
    } else if (!_.isEqual(nextProps.pageConfig, prevState.pageConfig)) {
      return { pageConfig: nextProps.pageConfig };
    }
    return null;
  }

  componentDidUpdate(prevProps, prevState) {
    if (!_.isEqual(this.state.pageConfig, prevState.pageConfig)) {
      this.updatePage();
    }
  }

  changeLayout = (
    updates,
    isPageConfig,
    isPageConfigPartialUpdate = false,
    afterChangeLayout = _.noop,
    lockLayout = false
  ) => {
    if (_.find(updates, (item, key) => !_.has(names, key))) {
      throw new Error('Invalid layout configs');
    }

    if (this.state.isLayoutLocked) {
      return;
    }
    this.state.isLayoutLocked = lockLayout;

    if (isPageConfig) {
      this.setState((state) => {
        const pageConfig = isPageConfigPartialUpdate ?
          merge(cloneDeep(state.pageConfig), updates) :
          merge(cloneDeep(state.persistConfig), updates);
        return {
          pageConfig: _.isEqual(pageConfig, state.pageConfig) ? state.pageConfig : pageConfig,
          ownUpdate: true,
        };
      }, () => {
        this.updatePage(afterChangeLayout);
      });
    } else {
      this.setState((state) => {
        const persistConfig = merge(cloneDeep(state.persistConfig), updates);
        const pageConfig = merge(cloneDeep(state.pageConfig), updates);
        return {
          persistConfig: _.isEqual(persistConfig, state.persistConfig) ?
            state.persistConfig :
            persistConfig,
          pageConfig: _.isEqual(pageConfig, state.pageConfig) ? state.pageConfig : pageConfig,
          ownUpdate: true,
        };
      }, () => {
        this.updatePage(afterChangeLayout);
      });
    }
  };

  updatePage(afterChangeLayout = _.noop) {
    if (this.stylesContainer) {
      this.stylesContainer.update(this.state.pageConfig);
    }
    afterChangeLayout();
  }

  render() {
    return (
      <LayoutContext.Provider value={{
        config: this.state.pageConfig,
        styles: this.stylesContainer ? this.stylesContainer.styles : {},
        isGridLayout: this.props.isGridLayout,
        change: this.changeLayout,
      }}
      >
        {this.props.isGridLayout ? (
          <div className={classNames('app-root-container', this.props.classes.container)}>
            {this.props.children}
          </div>)
        : this.props.children}
      </LayoutContext.Provider>
    );
  }
}

const StyledLayoutContainer = withDefaultStyles(LayoutContainer, () => styles);

export { StyledLayoutContainer as LayoutContainer };
